import { useEffect, useRef } from 'react'
import { Avatar } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faVanShuttle, faTaxi, faCarSide, faUserGroup, faSatelliteDish } from '@fortawesome/free-solid-svg-icons'
import { progress, passengerCount, localityName, formatTime, formatMoney } from './tripUtils'

const vehicle = type => {
  if (type === 'van') return { icon: faVanShuttle, label: 'Van' }
  if (type === 'taxi') return { icon: faTaxi, label: 'Taxi' }
  return { icon: faCarSide, label: 'Carpool' }
}

const TripCard = ({ trip, now, onOpen }) => {
  const pct = Math.round(progress(trip, now) * 100)
  const pax = passengerCount(trip)
  const v = vehicle(trip.type)
  const money = formatMoney(trip.price, trip.currency)

  return (
    <div
      onClick={() => onOpen && onOpen(trip._id)}
      className='live-card group rounded-2xl p-3.5 mb-2.5 border cursor-pointer transition-all duration-200 hover:-translate-y-0.5'
      style={{ background: 'var(--panel)', borderColor: 'var(--border)', boxShadow: 'var(--shadow-card)' }}
    >
      {/* Conductor + precio */}
      <div className='flex items-center gap-2.5'>
        <span className='flex items-center justify-center w-8 h-8 rounded-lg shrink-0' style={{ background: 'var(--coral-soft)', color: 'var(--coral)' }}>
          <FontAwesomeIcon icon={v.icon} className='text-sm' />
        </span>
        <div className='p-[1.5px] rounded-full shrink-0' style={{ background: 'linear-gradient(135deg,var(--amber),var(--coral))' }}>
          <Avatar
            src={trip?.Owner_info?.picture || undefined}
            sx={{ width: 30, height: 30, fontSize: 13, fontWeight: 700, background: 'var(--panel-soft)', color: 'var(--text-soft)', border: '2px solid var(--panel)' }}
          >
            {trip?.Owner_info?.first_name?.charAt(0)}
          </Avatar>
        </div>
        <div className='min-w-0 leading-tight'>
          <div className='text-sm font-bold truncate' style={{ color: 'var(--text)' }}>{trip?.Owner_info?.first_name || 'Conductor'}</div>
          <div className='flex items-center gap-1 text-[11px] font-semibold' style={{ color: 'var(--green)' }}>
            <FontAwesomeIcon icon={faUserGroup} className='text-[9px]' /> {pax} pasajero{pax === 1 ? '' : 's'}
          </div>
        </div>
        <div className='ml-auto text-right shrink-0'>
          {money && <div className='text-[15px] font-black tracking-tight' style={{ color: 'var(--text)' }}>{money}</div>}
          <span className='inline-block mt-0.5 text-[10px] font-bold px-2 py-0.5 rounded-full' style={{ background: 'var(--coral-soft)', color: 'var(--coral)' }}>{v.label}</span>
        </div>
      </div>

      <div className='my-2.5 border-t' style={{ borderColor: 'var(--border)' }} />

      {/* Ruta */}
      <div className='flex gap-2.5'>
        <div className='flex flex-col items-center pt-1'>
          <span className='w-2.5 h-2.5 rounded-full' style={{ background: 'var(--coral)' }} />
          <span className='flex-1 w-0.5 my-0.5' style={{ background: 'var(--border-strong)' }} />
          <span className='w-2.5 h-2.5 rounded-full border-2' style={{ borderColor: 'var(--coral)', background: 'var(--panel)' }} />
        </div>
        <div className='flex-1 min-w-0 text-[13px]'>
          <div className='flex items-center justify-between'>
            <span className='font-semibold truncate' style={{ color: 'var(--text)' }}>{localityName(trip?.Journey?.origin_name)}</span>
            <span className='tabular-nums shrink-0 ml-2' style={{ color: 'var(--text-soft)' }}>{formatTime(trip?.Journey?.departure_time)}</span>
          </div>
          <div className='flex items-center justify-between mt-3.5'>
            <span className='font-semibold truncate' style={{ color: 'var(--text)' }}>{localityName(trip?.Journey?.destination_name)}</span>
            <span className='tabular-nums shrink-0 ml-2' style={{ color: 'var(--text-soft)' }}>{formatTime(trip?.Journey?.estimated_time)}</span>
          </div>
        </div>
      </div>

      {/* Progreso en curso */}
      {pct > 0 && pct < 100 && (
        <div className='mt-2.5 h-1 rounded-full overflow-hidden' style={{ background: 'var(--track)' }}>
          <div className='h-full rounded-full' style={{ width: `${pct}%`, background: 'linear-gradient(90deg,var(--amber),var(--coral))' }} />
        </div>
      )}
    </div>
  )
}

const TripsPanel = ({ liveTrips = [], now, onOpenTrip }) => {
  const scrollRef = useRef(null)

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    let raf, dir = 1, pause = 0, last = performance.now()
    const speed = 14
    const tick = ts => {
      const dt = (ts - last) / 1000
      last = ts
      const overflow = el.scrollHeight - el.clientHeight
      if (overflow > 8) {
        if (pause > 0) pause -= dt
        else {
          el.scrollTop += dir * speed * dt
          if (el.scrollTop >= overflow - 1) { dir = -1; pause = 2.5; el.scrollTop = overflow }
          else if (el.scrollTop <= 1) { dir = 1; pause = 2.5; el.scrollTop = 0 }
        }
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [liveTrips.length])

  return (
    <div className='h-full flex flex-col min-h-0'>
      <div className='flex items-center gap-2.5 mb-3 shrink-0'>
        <span className='live-blink w-2 h-2 rounded-full' style={{ background: 'var(--coral)' }} />
        <h2 className='font-bold text-[15px]' style={{ color: 'var(--text)' }}>Viajes en curso</h2>
        <span className='ml-auto flex items-center justify-center min-w-[26px] h-6 px-2 text-sm font-bold tabular-nums rounded-full' style={{ background: 'var(--coral-soft)', color: 'var(--coral)' }}>
          {liveTrips.length}
        </span>
      </div>
      <div ref={scrollRef} className='live-scroll flex-1 overflow-y-auto pr-1 min-h-0'>
        {liveTrips.length === 0 ? (
          <div className='h-full flex flex-col items-center justify-center text-center px-6' style={{ color: 'var(--text-faint)' }}>
            <span className='flex items-center justify-center w-14 h-14 rounded-2xl mb-3' style={{ background: 'var(--panel-soft)', border: '1px solid var(--border)' }}>
              <FontAwesomeIcon icon={faSatelliteDish} className='text-xl opacity-60' />
            </span>
            <p className='text-sm font-medium' style={{ color: 'var(--text-soft)' }}>No hay viajes en curso ahora.</p>
          </div>
        ) : (
          liveTrips.map(trip => <TripCard key={trip._id} trip={trip} now={now} onOpen={onOpenTrip} />)
        )}
      </div>
    </div>
  )
}

export default TripsPanel
